import { useAction, usePaginatedQuery, useQuery } from "convex/react";
import { RefreshCwIcon, SearchIcon } from "lucide-react";
import {
	parseAsInteger,
	parseAsString,
	parseAsStringLiteral,
	useQueryState,
} from "nuqs";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TooltipProvider } from "@/components/ui/tooltip";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { AccountSwitcher } from "./account-switcher";
import { MatterDetail } from "./matter-detail";
import { MatterFilters } from "./matter-filters";
import { MatterList } from "./matter-list";
import type { ClioAccount } from "./types";

const STATUS_TAB_VALUES = ["all", "Open", "Pending", "Closed"] as const;
type StatusTab = (typeof STATUS_TAB_VALUES)[number];

interface ClioProps {
	accounts: ClioAccount[];
	defaultLayout?: number[];
}

export function Clio({ accounts, defaultLayout = [22, 30, 48] }: ClioProps) {
	const [selectedConnectionId, setSelectedConnectionId] = useState<
		Id<"oauthConnections">
	>(() => accounts[0].connectionId);
	const [statusTab, setStatusTab] = useQueryState<StatusTab>(
		"status",
		parseAsStringLiteral(STATUS_TAB_VALUES).withDefault("all"),
	);
	const [searchText, setSearchText] = useQueryState(
		"q",
		parseAsString.withDefault(""),
	);
	const [selectedMatterClioId, setSelectedMatterClioId] = useQueryState(
		"matter",
		parseAsInteger,
	);
	const [responsibleAttorneyClioId, setResponsibleAttorneyClioId] =
		useQueryState("attorney", parseAsInteger);
	const [practiceAreaClioId, setPracticeAreaClioId] = useQueryState(
		"practice",
		parseAsInteger,
	);

	const listArgs = {
		connectionId: selectedConnectionId,
		status: statusTab === "all" ? undefined : statusTab,
		searchText: searchText.trim() || undefined,
		responsibleAttorneyClioId: responsibleAttorneyClioId ?? undefined,
		practiceAreaClioId: practiceAreaClioId ?? undefined,
	};

	const pagination = usePaginatedQuery(api.clio.queries.listMatters, listArgs, {
		initialNumItems: 30,
	});

	const matterDetail = useQuery(
		api.clio.queries.getMatter,
		selectedMatterClioId !== null
			? {
					connectionId: selectedConnectionId,
					clioMatterId: selectedMatterClioId,
				}
			: "skip",
	);

	const syncStatus = useQuery(api.clio.queries.getSyncStatus, {
		connectionId: selectedConnectionId,
	});

	const triggerFullSync = useAction(api.clio.actions.triggerFullSync);
	const [syncing, setSyncing] = useState(false);
	const [syncMessage, setSyncMessage] = useState<string | null>(null);

	const onSyncClick = async () => {
		setSyncing(true);
		setSyncMessage(null);
		try {
			const result = await triggerFullSync({
				connectionId: selectedConnectionId,
			});
			setSyncMessage(
				result.scheduled
					? "Sync started…"
					: result.reason === "already_running"
						? "Sync already in progress"
						: "Not scheduled",
			);
		} catch (err) {
			setSyncMessage(err instanceof Error ? err.message : "Sync failed");
		} finally {
			setSyncing(false);
		}
	};

	const matterState = syncStatus?.find((s) => s.resource === "matters");
	const lastSyncedAt = matterState?.lastSyncedAt;
	const progressCutoff = Date.now() - 2 * 60 * 1000;
	const anyRunning =
		syncStatus?.some(
			(s) => s.status === "running" && s.updatedAt >= progressCutoff,
		) ?? false;
	const isUpToDate =
		!anyRunning &&
		matterState?.status === "completed" &&
		lastSyncedAt !== undefined &&
		Date.now() - lastSyncedAt < 5 * 60 * 1000;

	return (
		<TooltipProvider delay={0}>
			<ResizablePanelGroup
				orientation="horizontal"
				className="h-full min-h-0 items-stretch"
			>
				<ResizablePanel
					defaultSize={`${defaultLayout[0]}%`}
					minSize="18%"
					maxSize="30%"
					className="flex min-h-0 flex-col"
				>
					<div className="flex h-[52px] shrink-0 items-center px-3">
						<AccountSwitcher
							accounts={accounts}
							selectedConnectionId={selectedConnectionId}
							onSelect={(id) => {
								setSelectedConnectionId(id);
								setSelectedMatterClioId(null);
								setResponsibleAttorneyClioId(null);
								setPracticeAreaClioId(null);
							}}
						/>
					</div>
					<Separator />
					<ScrollArea className="min-h-0 flex-1">
						<div className="flex flex-col gap-3 p-3">
							<Button
								size="sm"
								variant="default"
								onClick={onSyncClick}
								disabled={syncing || anyRunning}
								className="w-full"
							>
								<RefreshCwIcon
									className={
										syncing || anyRunning ? "h-4 w-4 animate-spin" : "h-4 w-4"
									}
								/>
								{anyRunning ? "Syncing…" : "Sync now"}
							</Button>
							{(syncMessage || lastSyncedAt) && (
								<div className="flex flex-col gap-1 text-xs text-muted-foreground">
									{syncMessage && <div>{syncMessage}</div>}
									{lastSyncedAt && (
										<div className="flex items-center gap-2">
											<span>
												Last synced: {new Date(lastSyncedAt).toLocaleString()}
											</span>
											{isUpToDate && (
												<span className="inline-flex items-center rounded-full bg-success/15 px-1.5 py-0.5 text-[10px] font-medium text-success">
													Up to date
												</span>
											)}
										</div>
									)}
								</div>
							)}
						</div>
						<Separator />
						<MatterFilters
							connectionId={selectedConnectionId}
							responsibleAttorneyClioId={responsibleAttorneyClioId}
							practiceAreaClioId={practiceAreaClioId}
							onChangeAttorney={(id) => {
								setResponsibleAttorneyClioId(id);
								setSelectedMatterClioId(null);
							}}
							onChangePracticeArea={(id) => {
								setPracticeAreaClioId(id);
								setSelectedMatterClioId(null);
							}}
						/>
					</ScrollArea>
				</ResizablePanel>
				<ResizableHandle withHandle />
				<ResizablePanel defaultSize={`${defaultLayout[1]}%`} minSize="24%">
					<Tabs
						value={statusTab}
						onValueChange={(v) => {
							if (
								v === "all" ||
								v === "Open" ||
								v === "Pending" ||
								v === "Closed"
							) {
								setStatusTab(v);
								setSelectedMatterClioId(null);
							}
						}}
						className="flex h-full min-h-0 flex-col gap-0"
					>
						<div className="flex h-[52px] items-center px-4">
							<h1 className="text-xl font-bold">Matters</h1>
							<TabsList className="ml-auto">
								<TabsTrigger value="all">All</TabsTrigger>
								<TabsTrigger value="Open">Open</TabsTrigger>
								<TabsTrigger value="Pending">Pending</TabsTrigger>
								<TabsTrigger value="Closed">Closed</TabsTrigger>
							</TabsList>
						</div>
						<Separator />
						<div className="bg-background/95 p-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
							<div className="relative">
								<SearchIcon className="absolute top-2.5 left-2 h-4 w-4 text-muted-foreground" />
								<Input
									aria-label="Search matters"
									placeholder="Search matters…"
									className="pl-8"
									value={searchText}
									onChange={(e) => {
										setSearchText(e.target.value);
										setSelectedMatterClioId(null);
									}}
								/>
							</div>
						</div>
						<div className="m-0 flex min-h-0 flex-1 flex-col">
							<MatterList
								items={pagination.results}
								selectedClioId={selectedMatterClioId}
								onSelect={setSelectedMatterClioId}
								isLoading={pagination.status === "LoadingFirstPage"}
								canLoadMore={pagination.status === "CanLoadMore"}
								onLoadMore={() => pagination.loadMore(30)}
							/>
						</div>
					</Tabs>
				</ResizablePanel>
				<ResizableHandle withHandle />
				<ResizablePanel defaultSize={`${defaultLayout[2]}%`}>
					<MatterDetail
						connectionId={selectedConnectionId}
						matter={matterDetail ?? null}
						isLoading={
							selectedMatterClioId !== null && matterDetail === undefined
						}
					/>
				</ResizablePanel>
			</ResizablePanelGroup>
		</TooltipProvider>
	);
}
